export type PerformanceTier = "low" | "balanced" | "high";

export interface DevicePerformance {
  tier: PerformanceTier;
  targetFps: number;
  maxDpr: number;
  isReducedMotion: boolean;
  fireTextParticles: number;
  flameSparks: number;
}

export function getDevicePerformance(): DevicePerformance {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {
      tier: 'balanced',
      targetFps: 45,
      maxDpr: 1.25,
      isReducedMotion: false,
      fireTextParticles: 14000,
      flameSparks: 1.0,
    };
  }

  const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  // Basic heuristics for performance tier
  const hardwareConcurrency = navigator.hardwareConcurrency || 4;
  const deviceMemory = (navigator as any).deviceMemory || 4;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || (window.innerWidth < 768 && 'ontouchstart' in window);

  let tier: PerformanceTier = 'balanced';
  
  if (isMobile || hardwareConcurrency <= 4 || deviceMemory <= 4) {
    tier = 'low';
  } else if (hardwareConcurrency >= 8 && deviceMemory >= 8 && !isMobile) {
    tier = 'high';
  }

  let targetFps = 45;
  let maxDpr = 1.25;
  let fireTextParticles = 14000;
  let flameSparks = 1.0;

  if (tier === 'low') {
    targetFps = 30;
    maxDpr = 1.0;
    fireTextParticles = 8000;
    flameSparks = 0.0; // Disable sparks entirely on low-end
  } else if (tier === 'high') {
    targetFps = 60;
    maxDpr = 2.0; // Allow full 2.0 DPR on high-end
    fireTextParticles = 20000;
    flameSparks = 1.5;
  }

  return {
    tier,
    targetFps,
    maxDpr,
    isReducedMotion,
    fireTextParticles,
    flameSparks,
  };
}

export function createFpsThrottler(targetFps: number) {
  const frameInterval = 1000 / targetFps;
  let lastFrameTime = performance.now();

  return {
    shouldRender: (now: number) => {
      const elapsed = now - lastFrameTime;
      if (elapsed >= frameInterval) {
        lastFrameTime = now - (elapsed % frameInterval);
        return true;
      }
      return false;
    },
    reset: (now: number = performance.now()) => {
      lastFrameTime = now;
    }
  };
}
